<template>
    <div
        id="page-content"
        class="program-guide no-outline"
        tabindex="0" 
        @focus="onFocusPage"
    >
        <div v-if="featureGuideFilterEnabled" class="program-guide__filter">
            <guide-filter ref="guideFilterRef" />
        </div>
        <div class="program-guide__selected-program">
            <guide-quick-view-panel 
                :focused-program="focusedProgram"
                @go-restart="goRestart"
                @go-live="goLive" 
            />
        </div>
        <div 
            ref="wrapper" 
            class="program-guide__schedule-wrapper"
            :class="{'program-guide__schedule-wrapper_scrollbar': featureScrollbarEnabled}"
        >
            <guide-stage
                ref="guideStageRef"
                :observer-rows-key="componentKey"
                @load-prev-row="loadPrevRow"
                @load-next-row="loadNextRow" 
                @click-program="onClickProgram" 
                @on-focus-in="onFocusGuide"
                @on-focus-out="onBlurGuide"
                @is-ready-guide="hasReadyGuide"
                @on-update-height-guide="updateHeightGuide"
                @on-scroll-guide="onScrollGuide"
            />
            <div
                v-if="pendingLoading"
                class="program-guide__loader-overlay">
                <loader class="program-guide__loader" />
            </div>
        </div>
        <div v-if="featureScrollbarEnabled && isVisibleScrollbar" class="program-guide__scrollbar">
            <scrollbar
                ref="scrollbar"
                :config="configScrollbar"
                @on-scroll-scrollbar="onScrollGuide"
            />
        </div>
        <Teleport to="body">
            <guide-manager 
                v-if="isVisibilityGuideManager" 
                :channels="filteredChannels" 
                @founded="findChannelSegmentByChannel"
            />
        </Teleport>
    </div>
</template>

<script setup lang="ts">
    import type { GuideStageRef } from "./components/GuideStage/types";
    import { isTabletDevice } from "@ca.vmedia.tv/domains";
    import {linkItems} from "@/components/Header/types";
    import {ProgramUtils, TimeUtils} from "@/utils";
    import {ImagesCacheUtils} from "./utils";
    import { useApplicationStore } from "@/stores/Application";
    import { Strings } from "@/locales/types/enums/strings";
    import type { ScrollbarRef } from "./components/Scrollbar/types";
    import type { ConfigScrollbar } from "./components/Scrollbar/types/interfaces";
    import {type scrollGuideEvent, scrollTarget, type ISizeGuide, Direction} from "./types";
    import { useFocus } from "@/composables/useFocus";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { type Ref, ref, computed, nextTick, watch, getCurrentInstance, onMounted, onBeforeUnmount } from "vue";
    import { useRoute, useRouter } from 'vue-router';
    import { useI18n } from "vue-i18n";
    import { storeToRefs } from 'pinia';
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useFavoriteChannelsStore } from "@/stores/FavoriteChannels";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { usePlayerStore } from "@/stores/Player";
    import { useGuideStore } from "@/stores/Guide";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { useClientMessagesStore } from "@/stores/ClientMessages";
    import { useFavorite } from "@/composables/useFavorite";
    import type { GuideFilterRef } from "./components/GuideFilter/types";
    import { FilterTabsType } from "@/stores/Guide/types";

    const { deleteSavedIdsFavorites } = useFavorite();

    const ClientMessagesStore = useClientMessagesStore();
    const {
        isModalVisible: isModalMessagesShown,
    } = storeToRefs(ClientMessagesStore);

    const { 
        setViewedDetailItems
    } = useDetailPageStore();


    const ApplicationStore = useApplicationStore();
    const {
        hideHeader,
        showPreviewSignInModal,
        setTooltipeShowing,
        setTooltipeMessage,
    } = ApplicationStore;

    const GuideStoreModule = useGuideStore();
    const {
        fetchPrevChannelsSegments,
        fetchNextChannelsSegments,
        setState,
        initState,
        fetchFilteredChannels,
        fetchChannelsSegments,
        updatePrograms: updateProgramsInStore,
        setReducedSizeConfig,
        setGuideSize: setGuideSizeInStore,
        clearChannelsViewport,
        clearFocusedProgram,
        deletePrograms,
        fetchChannelsSegmentsByChannelId,
        addOffsetLastFocusedProgram,
        addOffsetFirstFocusedProgram,
        addOffsetFocusedProgram
    } = GuideStoreModule;
    const {
        filterParams,
        channelsViewPortIsEmpty,
        channelsViewPortIsNull,
        channelsViewPort: guideGrid,
        guideFilteredChannels: filteredChannels,
        configProgram: getCurrentConfigProgram,
        focusedChannel,
        focusedProgram,
        pendingLoading,
        getIsFromRoutePlayer,
        timelineBarTimes,
        timeMarkInterval
    } = storeToRefs(GuideStoreModule)

    const { focusToHeader: focusToHeaderGlobal } = useFocus();

    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const FavoriteChannelsStore = useFavoriteChannelsStore();
    const {
        fetchFavChannelsIds,
    } = FavoriteChannelsStore;

    const PlayerStoreModule = usePlayerStore();
    const {
        setPlayFromBeginning,
        startPlayingProgram
    } = PlayerStoreModule;

    const internalInstance = getCurrentInstance();
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const {guideConfig, featureHeaderEnabled} = useAppConfig();
    const {$navigator, $eventBus} = useNuxtApp();
    const {
        featureMoveLeftRightEnabled,
        featureGuideDetailPageEnabled,
        featureBtnWatchEnabled,
        featureGuideFilterEnabled,
        featureScrollbarEnabled,
        channelsPageSize,
        featureUpdateViewportChannelsEnabled
    } = guideConfig;
    const { setCurrentTimes, needsUnlockPin } = useParentControl();

    const handle: Ref<null | (() => void)> = ref(null);

    const sizeGuide: Ref<ISizeGuide> = ref({
        width: '0px',
        height: '0px',
    });
    const isReadyGuide: Ref<boolean> = ref(false);
    const componentKey: Ref<number> = ref(TimeUtils.dateNow());
    const isVisibleScrollbar: Ref<boolean> = ref(false);
    const heightGuide: Ref<number> = ref(0);
    const cleaningChannelsProgramEnable: Ref<boolean> = ref(false);
    const isRegKeyListenersGuide: Ref<boolean> = ref(false);
    const recentKeyEventTime: Ref<number> = ref(0);
    const scrollOffsetInPercent: Ref<number> = ref(0);
    const ratioHeightThumb: Ref<number> = ref(0);
    const isEnabledFocusToGuide: Ref<boolean> = ref(true);
    const guideStageRef: Ref<GuideStageRef | null> = ref(null);
    const scrollbarRef: Ref<ScrollbarRef | null> = ref(null);
    const wrapper: Ref<HTMLElement | null> = ref(null);
    const isUpdatedViewportChannels: Ref<boolean> = ref(false);
    const guideFilterRef: Ref<GuideFilterRef | null> = ref(null);

    const isVisibilityGuideManager  = computed((): boolean => !isTypeModeEnabledPin.value);

    const configScrollbar = computed((): ConfigScrollbar => {
        return {
            'height': heightGuide.value,
            'scrollOffset': scrollOffsetInPercent.value,
            'ratioHeightThumb': ratioHeightThumb.value
        };
    });
    const isEmptyGuide  = computed((): boolean => channelsViewPortIsEmpty.value || channelsViewPortIsNull.value);
    const isFirstChannelFromFiltered = computed((): boolean => guideGrid.value?.[0].channel?.id === filteredChannels.value[0]?.id);

    const focusedChannelIndex = computed((): number => {
        const [channelIdx] = getCurrentConfigProgram.value;
        return channelIdx ;
    });

    const findChannelSegmentByChannel = (channelId: number) => {
        if(!channelId){
            Logger.captureError(new Error("Channel ID is missing"));
            return;
        } 

        clearChannelsViewport();
        clearFocusedProgram();

        void nextTick( async() => {
            await fetchChannelsSegmentsByChannelId(true, channelId);
            componentKey.value = TimeUtils.dateNow();
            focusToGuide(true);
        });
    };

    const onScrollGuide = (event: scrollGuideEvent): void => {
        if (event.target === scrollTarget.GUIDE) {
            scrollOffsetInPercent.value = event.offset;
            ratioHeightThumb.value = event.ratioHeightThumb;
        }

        if (event.target === scrollTarget.SCROLLBAR) {
            if (guideStageRef.value) {
                guideStageRef.value.scrollToGuide(event.offset);
                isEnabledFocusToGuide.value = false;
            }
        }
    };
    const updateHeightGuide = (height: number): void => {
        if (height > 0) {
            heightGuide.value = height;
            isVisibleScrollbar.value = true;
        }
    };
    const hasReadyGuide = (value: boolean): void => {
        isReadyGuide.value = value;
    };
    const onFocusGuide = (): void => {
        if(isEmptyGuide.value){
            return;
        }
        regKeyListenersGuide();
    };
    const onBlurGuide = (): void => {
        if(isEmptyGuide.value){
            return;
        }
        unregKeyListenersGuide();
    };
    const resetUrl = () => {
        // to clear to work "clearStore"
        if (route.fullPath !== linkItems.guide.routerPath) {
            void router.replace({ 
                path: linkItems.guide.routerPath, 
            });
        }
    };
    provide('resetUrl', resetUrl);

    const focusToHeader = () => {
        resetUrl();
        focusToHeaderGlobal();
    };
    const focusToFirstElement = () => {
        if (isModalMessagesShown.value) {
            return;
        }
        if (featureHeaderEnabled) {
            hideHeader();
        }

        if (featureGuideFilterEnabled) {
            focusToFilter();
        } else {
            focusToGuide();
        }
    };
    const focusToGuide = (immediate: boolean = false): void => {
        if (!isEnabledFocusToGuide.value) {
            return;
        }

        if(immediate) {
            setFocusProgram(getCurrentConfigProgram.value);
            return;
        }

        if (channelsViewPortIsEmpty.value && filterParams.value === FilterTabsType.FAVOURITES) {
            document.getElementById("btnEmptyDataFavorites")?.focus();
            return;
        }

        if (isUpdatedViewportChannels.value) {
            // eslint-disable-next-line prefer-const
            let [channelIdx, startime] = getCurrentConfigProgram.value;
            const countItems = guideGrid.value ? guideGrid.value.length - 1 : 0;
            const isNotFullSet = guideGrid.value ? guideGrid.value.length < channelsPageSize : false;
            let targetConfig: number[] | null = null;
            let isFirstTime = true;

            if (channelIdx === 0 && isFirstTime) {
                channelIdx = countItems;
                targetConfig = getTargetConfig([channelIdx, startime], Direction.DOWN);
                isFirstTime = false;
            }

            if ((channelIdx === countItems || isNotFullSet) && isFirstTime) {
                channelIdx = 0;
                targetConfig = getTargetConfig([channelIdx, startime], Direction.UP);
                isFirstTime = false;
            }

            if (targetConfig !== null) {
                setFocusProgram(targetConfig);
                isUpdatedViewportChannels.value = false;
                return;
            }
        }

        const queryParams = route.query;
        if (queryParams.channelIdx === undefined || getIsFromRoutePlayer.value) {
            if (guideGrid.value === null || guideGrid.value.length === 0) {
                return;
            }

            const [channelIdx, startime] = getCurrentConfigProgram.value;
            if (!guideGrid.value[channelIdx] || !guideGrid.value[channelIdx].items) {
                return;
            }
            const programs = [...guideGrid.value[channelIdx].items];
            const reversedPrograms = programs.reverse();
            const targetProgram = reversedPrograms.find((program) => program.recentAirTime?.startTime != null ? program.recentAirTime?.startTime <= startime : null);
            if (targetProgram && targetProgram.recentAirTime?.startTime) {
                setFocusProgram([channelIdx, targetProgram.recentAirTime?.startTime]);
            }
        } else {
            const [channelIdx, startime] = getCurrentConfigProgram.value;
            setFocusProgram([channelIdx, startime]);
        }
    };
    const focusToFilter = () => {
        if (guideFilterRef.value) {
            guideFilterRef.value.focusToFilterTabs();
        }
    };
    provide('focusToFilter', focusToFilter);
    const onClickProgram = (): void => {
        if (featureBtnWatchEnabled) {
            return;
        }
        startWatch();
    };
    const loadPrevRow = (): void => {
        fetchPrevChannelsSegments(true, false);
    };
    const loadNextRow = (): void => {
        fetchNextChannelsSegments(true, false);
    };
    const onFocusPage = () => {
        if (isTabletDevice) {
            return;
        }

        if (featureHeaderEnabled) {
            hideHeader();
        }

        
        if (isEmptyGuide.value) {
            focusToFirstElement();
        } else {
            focusToGuide();
        }
    };
    const onFocusToHeader = (): void => {
        if (featureHeaderEnabled) {
            focusToHeader();
        }
    };
    const keyDownListenerBtnFilter = (): void => {
        focusToGuide();
    };
    provide('keyDownListenerBtnFilter', keyDownListenerBtnFilter);

    const regKeyListenersGuide = () => {
        if (isRegKeyListenersGuide.value) {
            return;
        }
        $navigator.on("keypress:up", keyUpListenerGuide);
        $navigator.on("keypress:down", keyDownListenerGuide);
        $navigator.on("keypress:left", keyLeftListenerGuide);
        $navigator.on("keypress:right", keyRightListenerGuide);
        $navigator.on("keypress:back", keyBackListenerGuide);
        isRegKeyListenersGuide.value = true;

    };
    const unregKeyListenersGuide = () => {
        if (!isRegKeyListenersGuide.value) {
            return;
        }
        $navigator.off("keypress:up", keyUpListenerGuide);
        $navigator.off("keypress:down", keyDownListenerGuide);
        $navigator.off("keypress:left", keyLeftListenerGuide);
        $navigator.off("keypress:right", keyRightListenerGuide);
        $navigator.off("keypress:back", keyBackListenerGuide);
        
        isRegKeyListenersGuide.value = false;

    };
    const findPreviousTime= (startime: number) => [...timelineBarTimes.value].reverse().find((time) => time <= startime) ??  timelineBarTimes.value[0];
    const getTargetConfig = (configProgram: number[], direction: Direction.UP | Direction.DOWN | Direction.LEFT | Direction.RIGHT): number[] | null => {
        if (guideGrid.value === null || guideGrid.value.length === 0) {
            return null;
        }

        const [channelIdx, startime] = configProgram;
        if (!guideGrid.value[channelIdx] || !guideGrid.value[channelIdx].items) {
            return null;
        }
        const programs = [...guideGrid.value[channelIdx].items];
        let newStartime: number | null | undefined = null;

        if (direction === Direction.UP || direction === Direction.DOWN) {
            let targetProgram = programs.reverse().find((program) => program.recentAirTime?.startTime != null ? program.recentAirTime?.startTime <= startime : null);

            if (targetProgram === undefined) {
                targetProgram = programs[programs.length - 1];
            }
            newStartime = targetProgram ? targetProgram.recentAirTime?.startTime : startime;
        }

        if (direction === Direction.LEFT) {
            const reversedPrograms = programs.reverse();
            const targetProgram = reversedPrograms.find((program) => program.recentAirTime?.startTime != null ? program.recentAirTime?.startTime < startime : null);
            newStartime = targetProgram ? targetProgram.recentAirTime?.startTime : startime;
        }

        if (direction === Direction.RIGHT) {
            const targetProgram = programs.find((program) => program.recentAirTime?.startTime != null ? program.recentAirTime?.startTime > startime : null);
            newStartime = targetProgram ? targetProgram.recentAirTime?.startTime : startime;
        }

        if (newStartime) {
            return [channelIdx, newStartime];
        } else {
            return null;
        }
    };
    const setFocusProgram = (coord: number[] | null): void => {
        if (isModalMessagesShown.value) {
            return;
        }
        
        if (!coord || coord.length === 0) {
            return;
        }

        void nextTick(() => {
            const [channelIdx, starTime] = coord;
            const nextProgram: HTMLElement | null = document.getElementById(`${channelIdx}_${starTime}`);
            if (nextProgram) {
                nextProgram.focus();
            }
        });
    };
    const moveGuideFocusToUp = (): void => {
        let [channelIdx, startime] = getCurrentConfigProgram.value;
        startime = findPreviousTime(startime);

        if (isFirstChannelFromFiltered.value && channelIdx === 0) {
            return;
        }

        channelIdx -= 1;
        const targetConfig = getTargetConfig([channelIdx, startime], Direction.UP);
        if (targetConfig !== null) {
            setFocusProgram(targetConfig);
        }
    };
    const moveGuideFocusToDown = (): void => {
        let [channelIdx, startime] = getCurrentConfigProgram.value;
        startime = findPreviousTime(startime);

        if (featureUpdateViewportChannelsEnabled) {
            channelIdx = channelIdx === channelsPageSize - 1 ? 0 : channelIdx + 1;
        } else {
            channelIdx += 1;
        }
        
        const targetConfig = getTargetConfig([channelIdx, startime], Direction.DOWN);
        if (targetConfig !== null) {
            setFocusProgram(targetConfig);
        }
    };
    const moveGuideFocusToLeft = (): void => {
        const targetConfig = getTargetConfig(getCurrentConfigProgram.value, Direction.LEFT);

        if (targetConfig !== null) {
            setFocusProgram(targetConfig);
        }
    };
    const moveGuideFocusToRight = (): void => {
        const targetConfig = getTargetConfig(getCurrentConfigProgram.value, Direction.RIGHT);

        if (targetConfig !== null) {
            setFocusProgram(targetConfig);
        }
    };
    const updateViewportChannels = (channelIdx: number) => {
        if (channelIdx === 0) {
            loadPrevRow();
        }
        if (channelIdx === channelsPageSize - 1) {
            loadNextRow();
        }

        isUpdatedViewportChannels.value = true;
    };
    const keyUpListenerGuide = () => {
        if (featureScrollbarEnabled) {
            if (scrollbarRef.value) {
                scrollbarRef.value.onPrevClick();
            }
            return;
        }

        if (isFirstChannelFromFiltered.value && focusedChannelIndex.value === 0 && featureGuideFilterEnabled) {
            focusToFilter();
        } else {
            throttle(() => {
                const [channelIdx] = getCurrentConfigProgram.value;

                if (featureUpdateViewportChannelsEnabled && channelIdx === 0) {
                    updateViewportChannels(channelIdx);
                } else {
                    moveGuideFocusToUp();
                }
            });
        }
    };
    const throttle = (cb: () => void): void => {
        if (TimeUtils.dateNow() - recentKeyEventTime.value < 300) {
            return;
        }

        recentKeyEventTime.value = TimeUtils.dateNow();
        cb();
    };
    const keyDownListenerGuide = () => {
        if (featureScrollbarEnabled) {
            if (scrollbarRef.value) {
                scrollbarRef.value.onNextClick();
            }
            return;
        }

        throttle(() => {
            const [channelIdx] = getCurrentConfigProgram.value;

            if (featureUpdateViewportChannelsEnabled && channelIdx === channelsPageSize - 1) {
                updateViewportChannels(channelIdx);
            } else {
                moveGuideFocusToDown();
            }
        });
    };
    const keyLeftListenerGuide = () => {
        if (!featureMoveLeftRightEnabled) {
            return;
        };
        throttle(() => {
            moveGuideFocusToLeft();
            if(canOffsetForFirstProgram()){
                addOffsetFirstFocusedProgram(focusedProgram.value);
            } else {
                void nextTick(() => {
                    addOffsetFocusedProgram(focusedProgram.value);
                });
            }
        });
    };
    const keyRightListenerGuide = () => {
        if (!featureMoveLeftRightEnabled) {
            return;
        };
        throttle(() => {
            moveGuideFocusToRight();
            if(canOffsetForLastProgram()){
                addOffsetLastFocusedProgram(focusedProgram.value);
            } else {
                void nextTick(() => {
                    addOffsetFocusedProgram(focusedProgram.value);
                });
            }
        });
    };
    const canOffsetForLastProgram = (): boolean => {
        const channelIdx = focusedChannelIndex.value;
        const programs = guideGrid.value ? guideGrid.value[channelIdx].items : null;
        const programIdx = programs && focusedProgram.value && programs.findIndex(program => program.id === focusedProgram.value?.id);
        return (programs && programIdx !== -1 && programIdx === programs.length - 1) || (focusedProgram.value?.recentAirTime?.startTime  && focusedProgram.value?.lengthInS
        ? focusedProgram.value.recentAirTime.startTime + focusedProgram.value.lengthInS * 1000  > timelineBarTimes.value[timelineBarTimes.value.length - 1] + (timeMarkInterval.value / 2)
        : false);
    };
    const canOffsetForFirstProgram = (): boolean => {
        const channelIdx = focusedChannelIndex.value;
        const programs = guideGrid.value ? guideGrid.value[channelIdx].items : null;
        const programIdx = programs && focusedProgram.value && programs.findIndex(program => program.id === focusedProgram.value?.id);
        return programIdx === 0 && featureMoveLeftRightEnabled;
    };
    const checkParentControl = (): boolean => {
        const advisory = focusedProgram.value?.advisory ?? focusedChannel.value?.advisory;

        if (typeof advisory === 'number' &&  needsUnlockPin(advisory)) {
            ShowParentModal();
            SetTargetWidgetAdultControl(String(internalInstance?.uid));
            return false;
        } else {
            return true;
        }
    };
    const startWatch = (): void => {
        if (checkParentControl()){
            goToNextPage();
        }
    };
    const goRestart = (): void => {
        setPlayFromBeginning(true);
        startWatch();
    };
    const goLive = (): void => {
        setPlayFromBeginning(false);
        startWatch();
    };
    const keyBackListenerGuide = () => {
        onFocusToHeader();
    };
    const setGuideSize = () => {
        if (wrapper.value) {
            const { x, y } = wrapper.value.getBoundingClientRect();
            const { left , top} = wrapper.value.getBoundingClientRect();
            const wrapperWidth = x ?? left;
            const wrapperHeight = y ?? top;
            const width = window.innerWidth - wrapperWidth;
            const height = window.innerHeight - wrapperHeight;

            if (window.innerWidth <= 1366) {
                setReducedSizeConfig();
            }
            setGuideSizeInStore({ width, height });

            sizeGuide.value = {width: `${width}px`, height: `${height}px`};
        }

    };
    const goToPlayerPage = () => {
        try {
            if (!focusedProgram.value) {
                throw new Error(`FocusedProgram is ${focusedProgram.value}`);
            }

            startPlayingProgram(focusedProgram.value);
            cleaningChannelsProgramEnable.value = true;
            void router.push(`${linkItems.player.routerPath}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.PLAYER_TOOLTIP_ERROR_OPENING_PLAYER_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const goToDetailPage = () => {
        try {
            if (!focusedProgram.value) {
                throw new Error("FocusedProgram is null");
            }
            setViewedDetailItems(focusedProgram.value);
            void router.push(`${linkItems.detailsPrograms.routerPath}/${focusedProgram.value.recentAirTime!.id}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const goToNextPage = () => {
        if (focusedProgram.value && ProgramUtils.isLive(focusedProgram.value)) {            
            if (isGuest.value) {
                setTimeout(() => {
                    showPreviewSignInModal(true);
                }, 150);
                return;
            }
            goToPlayerPage();
        } else {
            if (!featureGuideDetailPageEnabled) {
                return;
            }

            if (focusedProgram.value?.id) {
                goToDetailPage();
            } else {
                goToPlayerPage();
            }
        }
    };
    const correctedPin = () => {
        goToNextPage();
        setCurrentTimes();
        SetIncorrectAdultPin();
    };
    const initGuide = (): void => {
        initState();
        fetchFilteredChannels();
        fetchChannelsSegments(true);
    };
    const updatePrograms = (): void => {
        setState();
        updateProgramsInStore();
    };
    const reInitGuide = (): void => {
        clearChannelsViewport();
        clearFocusedProgram();

        void nextTick(() => {
            initGuide();
            componentKey.value = TimeUtils.dateNow();
        });
    };

    watch(
        guideGrid,
        (newValue, oldValue) => {
            void nextTick(() => {
                if (isTabletDevice && oldValue === null) {
                    focusToFirstElement();
                }

                const channelIdx = route.query.channelIdx;
                const hasChannelIdx = channelIdx !== undefined && channelIdx !== null;
                if (newValue !== null && newValue.length > 0 && hasChannelIdx) {
                    setTimeout(() => {
                        focusToGuide();
                    }, 200);
                }
                isEnabledFocusToGuide.value = true;
            });
        }
    );
    watch(
        isAdultPinCorrect,
        (value: boolean) => {
            if (value && internalInstance?.uid === Number(targetWidgetAdultControl.value)) {
                correctedPin();
            }
        }
    );
    watch(
        filterParams,
        () => {
            reInitGuide();
        }
    );

    onMounted( async() => {
        $eventBus.on("leave-header", focusToFirstElement);     
        if (!isGuest.value) {
            await deleteSavedIdsFavorites();
            await fetchFavChannelsIds();
        }

        void nextTick(() => {
            if (isEmptyGuide.value){
                initGuide();
                if (!isModalMessagesShown.value) {
                    focusToHeader();
                }
            } else if (getIsFromRoutePlayer.value){
                updatePrograms();
            } else if (!getIsFromRoutePlayer.value){
                if (!isModalMessagesShown.value) {
                    focusToGuide();
                }
            }

            handle.value = () => {
                setGuideSize();
            };

            window.addEventListener('resize', handle.value);
            setGuideSize();
        });
    });

    onBeforeUnmount(() => {
        unregKeyListenersGuide();
        $eventBus.off("leave-header", focusToFirstElement);

        window.removeEventListener('resize', handle.value!);
        ImagesCacheUtils.clear();

        if (cleaningChannelsProgramEnable.value) {
            deletePrograms();
        }
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
